import { MountedPortal } from '#app/components/MountedPortal/MountedPortal.js';
import { animated } from '#app/components/Primitives/Animated.js';
import { springAnimation } from '#app/utilities/animation.js';
import {
  Arrow,
  TooltipContainer,
  TooltipContent,
  TooltipParagraph,
  TooltipReferenceContainer,
  TooltipSubtitle,
} from './Tooltip.styles.js';
import { type ArrowPosition, type TooltipCompoundProps } from './types.js';
import { useIsomorphicLayoutEffect } from '@contra/react-hooks/useIsomorphicLayoutEffect';
import { useMount } from '@contra/react-hooks/useMount';
import { type Placement } from '@popperjs/core';
import { useTooltip } from '@react-aria/tooltip';
import { AnimatePresence } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { useTheme } from 'styled-components';

const getInferredArrowPosition = (placement?: Placement): ArrowPosition => {
  switch (placement) {
    case 'bottom':
      return 'top-center';
    case 'left':
      return 'right-center';
    case 'right':
      return 'left-center';
    case 'top':
      return 'bottom-center';
    default:
      return 'top-center';
  }
};

export const Tooltip = ({
  arrowPosition,
  children,
  containerWidth = undefined,
  content,
  delay = 0,
  // the default is being set to 254 because all tooltips should have the same width/breakpoint
  disabled = false,
  disableOpenOnFocus = false,
  // per this ticket: https://linear.app/contra/issue/BREAD-562/%F0%9F%90%9B-tooltip-size-consistency
  hideReferenceContainerOutlineOnFocus = false,
  keepOpenOnHover = false,
  manualClose = false,
  offset = [0, 8],
  placement = 'top',
  strategy = 'fixed',
  tabIndex = 0,
  variant = 'black',
  whitespace = 'pre-line',
  width = 254,
  withPortal = false,
  ...props
}: TooltipCompoundProps) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const { tooltipProps } = useTooltip({
    isOpen,
    ...props,
  });
  const referenceElement = useRef<HTMLDivElement>(null);
  const popperElement = useRef<HTMLDivElement>(null);
  const { attributes, styles, update } = usePopper(
    referenceElement.current,
    popperElement.current,
    {
      modifiers: [
        {
          name: 'offset',
          options: {
            offset,
          },
        },
        {
          enabled: true,
          name: 'hide',
        },
      ],
      placement,
      strategy,
    },
  );

  useIsomorphicLayoutEffect(() => {
    if (isOpen && update) void update();
  }, [isOpen, update]);

  const timeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null);
  const hoverCounterRef = useRef(0);

  const openTooltip = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setIsOpen(true);
    }, delay);
  }, [delay, setIsOpen]);

  const closeTooltip = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setIsOpen(false);
  }, [setIsOpen]);

  useMount(() => {
    // Clear timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      setIsOpen(false);
    };
  });

  const handleMouseEnter = useCallback(() => {
    hoverCounterRef.current += 1;
    openTooltip();
  }, [openTooltip]);

  const handleMouseLeave = useCallback(() => {
    if (manualClose) {
      return;
    }

    hoverCounterRef.current -= 1;
    if (hoverCounterRef.current === 0 || !keepOpenOnHover) {
      hoverCounterRef.current = 0;
      closeTooltip();
    }
  }, [closeTooltip, keepOpenOnHover, manualClose]);

  const handlePopperMouseLeave = useCallback(() => {
    if (manualClose) {
      return;
    }

    hoverCounterRef.current = 0;
    closeTooltip();
  }, [closeTooltip, manualClose]);

  useEffect(() => {
    const referenceElementCurrent = referenceElement.current;
    const popperElementCurrent = popperElement.current;

    if (referenceElementCurrent) {
      referenceElementCurrent.addEventListener('mouseenter', handleMouseEnter);
      referenceElementCurrent.addEventListener('mouseleave', handleMouseLeave);
    }

    if (popperElementCurrent && keepOpenOnHover) {
      popperElementCurrent.addEventListener('mouseenter', handleMouseEnter);
      popperElementCurrent.addEventListener(
        'mouseleave',
        handlePopperMouseLeave,
      );
    }

    return () => {
      if (referenceElementCurrent) {
        referenceElementCurrent.removeEventListener(
          'mouseenter',
          handleMouseEnter,
        );
        referenceElementCurrent.removeEventListener(
          'mouseleave',
          handleMouseLeave,
        );
      }

      if (popperElementCurrent && keepOpenOnHover) {
        popperElementCurrent.removeEventListener(
          'mouseenter',
          handleMouseEnter,
        );
        popperElementCurrent.removeEventListener(
          'mouseleave',
          handlePopperMouseLeave,
        );
      }
    };
  }, [
    handleMouseEnter,
    handleMouseLeave,
    handlePopperMouseLeave,
    keepOpenOnHover,
  ]);

  useEffect(() => {
    const internalClose = (event: MouseEvent) => {
      if (
        !referenceElement.current?.contains(event.target as Node) &&
        !popperElement.current?.contains(event.target as Node)
      ) {
        closeTooltip();
      }
    };

    if (isOpen && manualClose) {
      document.addEventListener('click', internalClose);
    }

    return () => {
      document.removeEventListener('click', internalClose);
    };
  }, [closeTooltip, isOpen, manualClose, popperElement, referenceElement]);

  const tooltip = (
    <div
      ref={popperElement}
      style={{
        ...styles['popper'],
        pointerEvents: keepOpenOnHover || manualClose ? 'auto' : 'none',
        width: 'auto',
        zIndex: theme.zIndices.tooltip,
      }}
      {...attributes['popper']}
    >
      <AnimatePresence>
        {isOpen && !disabled ? (
          <animated.div
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            initial={{ opacity: 0, y: -8 }}
            transition={springAnimation.fast}
          >
            <TooltipContent
              as="div"
              maxWidth={width}
              role="tooltip"
              textStyle="bodyRegular"
              variant={variant}
              whitespace={whitespace}
              {...tooltipProps}
            >
              {arrowPosition ? (
                <Arrow
                  position={
                    arrowPosition === 'inferred'
                      ? getInferredArrowPosition(placement)
                      : arrowPosition
                  }
                  variant={variant}
                />
              ) : null}
              {content}
            </TooltipContent>
          </animated.div>
        ) : null}
      </AnimatePresence>
    </div>
  );

  return (
    <TooltipContainer $containerWidth={containerWidth}>
      <TooltipReferenceContainer
        $hideOutlineOnFocus={hideReferenceContainerOutlineOnFocus}
        onBlur={closeTooltip}
        onFocus={disableOpenOnFocus ? undefined : openTooltip}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={referenceElement}
        tabIndex={tabIndex}
      >
        {children}
      </TooltipReferenceContainer>
      {withPortal ? <MountedPortal>{tooltip}</MountedPortal> : tooltip}
    </TooltipContainer>
  );
};

Tooltip.Paragraph = TooltipParagraph;
Tooltip.Subtitle = TooltipSubtitle;
